import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import {
  useV2GetProjectByIdQuery,
  useV2ListProjectServicesQuery,
  useListProjectResourcesForAccountQuery,
  useListProjectLocationsQuery,
  useListProjectGovernancesQuery,
  useListLineOfBusinessesForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  capitalizeFirstLetter,
  renderResourceName,
} from "@utils/helperFunctions";
import { WorkBreakdownRow } from "./types/WorkBreakdownRow";
import Show from "./Show/Show";

interface Props {
  project_id: string;
}

interface TableSection {
  title: string;
  rows: WorkBreakdownRow[];
}

const WorkBreakdownV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const authorized = useAuthorization();

  const { data: project, isLoading: projectLoading } = useV2GetProjectByIdQuery({
    id: String(projectId),
    include: ["project-phases"],
  } as any);

  const { data: servicesData, isLoading: servicesLoading } =
    useV2ListProjectServicesQuery({
      filter: {
        project: projectId,
        "service-type": "professional_services",
        active: true,
      },
      include: [
        "project-location",
        "lob",
        "project-phase",
        "project-resource",
        "project-subservices",
        "project-subservices.project-resource",
      ] as any,
      page: { size: 500 },
    });

  const { data: projectResourcesData, isLoading: resourcesLoading } =
    useListProjectResourcesForAccountQuery({
      slug: accountSlug,
      filter: { project: projectId },
      include: ["line-of-business"],
      page: { size: 1000 },
    });

  const { data: locationsData, isLoading: locationsLoading } =
    useListProjectLocationsQuery({
      slug: accountSlug,
      filter: { project: projectId },
    } as any);

  const { data: governancesData, isLoading: governancesLoading } =
    useListProjectGovernancesQuery({
      slug: accountSlug,
      filter: { project: projectId },
      include: ["project-resource", "project-phase"],
    } as any);

  const { data: lobsData, isLoading: lobsLoading } =
    useListLineOfBusinessesForAccountQuery({ slug: accountSlug });

  const isLoading =
    !authorized ||
    projectLoading ||
    servicesLoading ||
    resourcesLoading ||
    locationsLoading ||
    governancesLoading ||
    lobsLoading;

  const attr = (project?.data?.attributes as any) || {};
  const paymentInfo = attr["payment-info"] || {};
  const rateTypeRaw = paymentInfo["rate-type"] || "hours";
  const rateType =
    rateTypeRaw !== "hours"
      ? capitalizeFirstLetter(rateTypeRaw) + "s"
      : capitalizeFirstLetter(rateTypeRaw);

  const allServices: any[] = (servicesData?.data as any[]) || [];
  const includedRecords: any[] = (servicesData?.included as any[]) || [];
  const projectResources: any[] = (projectResourcesData?.data as any[]) || [];
  const lobs: any[] =
    (projectResourcesData?.included as any[])?.filter(
      (item: any) => item.type === "line-of-businesses"
    ) || [];
  const allLobs: any[] = (lobsData?.data as any[]) || [];
  const locations: any[] = (locationsData?.data as any[]) || [];
  const governances: any[] = (governancesData?.data as any[]) || [];
  const phases: any[] =
    ((project as any)?.included || []).filter(
      (item: any) => item.type === "project-phases"
    ) || [];
  const linesOfBusiness: any[] = includedRecords.filter(
    (item) => item.type === "lobs"
  );
  const subservices: any[] = includedRecords
    .filter((item) => item.type === "project-subservices")
    .filter((sub) => sub.attributes.active === true);

  const resolveResource = (service: any): string => {
    if (service.attributes["task-source"] === "third_party") {
      return service.attributes["external-resource-name"] || "";
    }
    const relData = service.relationships?.["project-resource"]?.data;
    if (!relData) return "";
    const resource = projectResources.find(
      (r) => String(r.id) === String(relData.id)
    );
    if (resource) return renderResourceName(resource, lobs);
    return "";
  };

  const getHours = (service: any): number => {
    if (service.attributes["custom-hours?"] === true) {
      return parseFloat(service.attributes["extended-hours"]) || 0;
    }
    return parseFloat(service.attributes["override-hours"]) || 0;
  };

  const buildRowsForServices = (
    services: any[],
    groupRelationship: string,
    groupId: string
  ): WorkBreakdownRow[] => {
    const rows: WorkBreakdownRow[] = [];
    const matchedServices = services.filter((svc) => {
      const rel = svc.relationships?.[groupRelationship]?.data;
      return rel && String(rel.id) === String(groupId);
    });

    let grandTotalHours = 0;

    for (const svc of matchedServices) {
      const totalHours = parseFloat(svc.attributes["total-hours"]) || 0;
      grandTotalHours += totalHours;

      const svcResource = resolveResource(svc);

      const svcSubservices = subservices.filter(
        (sub) =>
          String(sub.relationships?.["project-service"]?.data?.id) ===
          String(svc.id)
      );

      const allResources: string[] = [svcResource];
      for (const sub of svcSubservices) {
        allResources.push(resolveResource(sub));
      }
      const nonEmpty = allResources.filter((r) => r !== "");
      const uniqueResources = new Set(nonEmpty);
      const summaryResource =
        uniqueResources.size === 0
          ? "---"
          : uniqueResources.size === 1
          ? [...uniqueResources][0]
          : "Various";

      rows.push({
        id: `svc-${svc.id}`,
        service: svc.attributes.name,
        resource: svcResource,
        quantity: svc.attributes.quantity || 0,
        hours: getHours(svc),
        total: parseFloat(svc.attributes["extended-hours"]) || 0,
        rowType: "data",
        summaryResource,
      });

      for (const sub of svcSubservices) {
        rows.push({
          id: `sub-${sub.id}`,
          service: sub.attributes.name,
          resource: resolveResource(sub),
          quantity: sub.attributes.quantity || 0,
          hours: getHours(sub),
          total: parseFloat(sub.attributes["extended-hours"]) || 0,
          rowType: "subservice",
        });
      }

      if (svcSubservices.length > 0) {
        rows.push({
          id: `subtotal-${svc.id}`,
          service: "",
          resource: "",
          quantity: 0,
          hours: totalHours,
          total: totalHours,
          rowType: "subtotal",
        });
      }
    }

    rows.push({
      id: `grand-${groupRelationship}-${groupId}`,
      service: "",
      resource: "",
      quantity: 0,
      hours: grandTotalHours,
      total: grandTotalHours,
      rowType: "grandTotal",
    });

    return rows;
  };

  const byLobData = useMemo((): TableSection[] => {
    if (isLoading) return [];
    return linesOfBusiness
      .filter((lob) =>
        allServices.some(
          (svc) =>
            svc.relationships?.lob?.data &&
            String(svc.relationships.lob.data.id) === String(lob.id)
        )
      )
      .map((lob) => ({
        title: lob.attributes.name,
        rows: buildRowsForServices(allServices, "lob", lob.id),
      }));
  }, [isLoading, linesOfBusiness, allServices, subservices, projectResources]);

  const byPhaseData = useMemo((): TableSection[] => {
    if (isLoading) return [];
    return phases
      .filter((phase) =>
        allServices.some(
          (svc) =>
            svc.relationships?.["project-phase"]?.data &&
            String(svc.relationships["project-phase"].data.id) ===
              String(phase.id)
        )
      )
      .map((phase) => ({
        title: phase.attributes.name,
        rows: buildRowsForServices(allServices, "project-phase", phase.id),
      }));
  }, [isLoading, phases, allServices, subservices, projectResources]);

  const byLocationData = useMemo((): TableSection[] => {
    if (isLoading) return [];
    return locations
      .filter((loc) =>
        allServices.some(
          (svc) =>
            svc.relationships?.["project-location"]?.data &&
            String(svc.relationships["project-location"].data.id) ===
              String(loc.id)
        )
      )
      .map((loc) => {
        const a = loc.attributes;
        const parts = [
          a.name ? `${a.name}:` : "",
          a.street,
          a.street2,
          a.city,
          a.state,
          a["postal-code"],
          a.country?.toUpperCase(),
        ].filter(Boolean);
        return {
          title: parts.join(" "),
          rows: buildRowsForServices(allServices, "project-location", loc.id),
        };
      });
  }, [isLoading, locations, allServices, subservices, projectResources]);

  const governanceRows = useMemo((): WorkBreakdownRow[] => {
    if (isLoading || governances.length === 0) return [];
    const rows: WorkBreakdownRow[] = governances.map((gov) => {
      const hours = parseFloat(gov.attributes.hours) || 0;
      let resource = "";
      const relData = gov.relationships?.["project-resource"]?.data;
      if (relData) {
        const res = projectResources.find(
          (r) => String(r.id) === String(relData.id)
        );
        if (res) resource = renderResourceName(res, lobs);
      }
      return {
        id: `gov-${gov.id}`,
        service: gov.attributes.description || "",
        resource,
        quantity: 1,
        hours,
        total: hours,
        rowType: "data" as const,
      };
    });

    const totalHours = rows.reduce((s, r) => s + r.hours, 0);
    rows.push({
      id: "gov-total",
      service: "",
      resource: "",
      quantity: 0,
      hours: totalHours,
      total: totalHours,
      rowType: "grandTotal",
    });

    return rows;
  }, [isLoading, governances, projectResources, lobs]);

  const showLocationTab = locations.length >= 2;

  if (isLoading) {
    return <ScopeStackSpinner />;
  }

  return (
    <Show
      rateType={rateType}
      byLobData={byLobData}
      byPhaseData={byPhaseData}
      byLocationData={byLocationData}
      governanceRows={governanceRows}
      showLocationTab={showLocationTab}
    />
  );
};

export default WorkBreakdownV2Mspa;
